/** Small, deterministic forms. Account identifiers are hashed locally, never embedded in artwork. */
export function avatarArt(seed: string, form?: number) {
  let hash = 2166136261;
  for (const character of seed) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619) >>> 0;
  const count = 5 + hash % 5;
  const turn = (hash >>> 8) % 360;
  const style = form === undefined ? (hash >>> 16) % 6 : Math.abs(Math.trunc(form)) % 6;
  const paths: { d: string; transform?: string; opacity?: number }[] = [];
  if (style === 0 || style === 1) {
    for (let index = 0; index < count; index++) paths.push({
      d: style === 0
        ? "M50 51C23 45 25 14 42 16C56 18 54 35 50 51Z"
        : "M47 51C33 37 29 17 42 16C55 15 59 35 53 51Z",
      transform: `rotate(${turn + index * 360 / count} 50 50)`,
    });
  } else if (style === 2) {
    for (let index = 0; index < 4; index++) paths.push({
      d: "M50 50V19C69 19 81 31 81 50H50Z", transform: `rotate(${turn + index * 90} 50 50)`,
      opacity: .45 + index * .18,
    });
  } else if (style === 3) {
    const angle = 12 + (hash >>> 3) % 17;
    for (let index = 0; index < 4; index++) paths.push({
      d: "M28 20H72V30H28Z", transform: `translate(0 ${index * 17}) rotate(${index % 2 ? -angle : angle} 50 25)`,
    });
  } else if (style === 4) {
    paths.push({ d: "M50 15C69 15 84 30 84 50S69 85 50 85S16 70 16 50S31 15 50 15ZM50 28C39 28 29 38 29 50S39 72 50 72S71 62 71 50S61 28 50 28Z" });
    paths.push({ d: "M45 39H55V61H45Z", transform: `rotate(${turn} 50 50)` });
  } else {
    for (let index = 0; index < count; index++) paths.push({
      d: "M47 16H53L58 43L84 47V53L57 57L53 84H47L43 57L16 53V47L43 43Z",
      transform: `rotate(${turn + index * 90 / count} 50 50)`, opacity: .3 + .7 * index / count,
    });
  }
  return paths;
}

/** Encode the selected form as an ordinary profile PNG for the existing account API. */
export async function avatarPNG(seed: string, palette: { background: string; foreground: string }, form?: number): Promise<string> {
  // Only locally defined paths and resolved CSS colors enter the SVG, never user text.
  const paths = avatarArt(seed, form).map(({ d, transform = "", opacity = 1 }) =>
    `<path d="${d}" transform="${transform}" opacity="${opacity}" fill-rule="evenodd"/>`).join("");
  const escape = (value: string) => value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 100 100"><path fill="${escape(palette.background)}" d="M0 0H100V100H0Z"/><g fill="${escape(palette.foreground)}">${paths}</g></svg>`;
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("avatar_canvas_unavailable");
    context.drawImage(image, 0, 0);
    return canvas.toDataURL("image/png");
  } finally { URL.revokeObjectURL(url); }
}
